import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, ScrollText } from "lucide-react";
import { useMemo, useState } from "react";

import { TechnicalDetails } from "@/components/TechnicalDetails";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import type { AuditEntry, AuditListParams } from "@tilt/admin-sdk";

type Preset = "1h" | "24h" | "7d" | "30d" | "all" | "custom";
const PRESET_LABEL: Record<Preset, string> = {
  "1h": "Last hour",
  "24h": "Last 24 h",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
  custom: "Custom",
};
const PRESET_MS: Record<Exclude<Preset, "all" | "custom">, number> = {
  "1h": 3600_000,
  "24h": 86400_000,
  "7d": 7 * 86400_000,
  "30d": 30 * 86400_000,
};

export function AuditLogPage(): JSX.Element {
  usePageTitle("Audit log");
  const { scope } = usePartnerScope();
  const [action, setAction] = useState("");
  const [resourceType, setResourceType] = useState("");
  // Default to Last hour so a fresh page load isn't a 7-day dump -
  // the common case is "what just happened right now". Operators
  // needing deeper history widen the filter explicitly.
  const [preset, setPreset] = useState<Preset>("1h");
  const [customSince, setCustomSince] = useState("");
  const [customUntil, setCustomUntil] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { since, until } = useMemo(() => {
    if (preset === "all") return { since: undefined, until: undefined };
    if (preset === "custom") {
      return {
        since: customSince ? new Date(customSince).toISOString() : undefined,
        until: customUntil ? new Date(customUntil).toISOString() : undefined,
      };
    }
    return {
      since: new Date(Date.now() - PRESET_MS[preset]).toISOString(),
      until: undefined,
    };
  }, [preset, customSince, customUntil]);

  const params = useMemo<AuditListParams>(() => {
    const p: AuditListParams = { limit: 100 };
    if (action.trim()) p.action = action.trim();
    if (resourceType.trim()) p.resource_type = resourceType.trim();
    if (since) p.since = since;
    if (until) p.until = until;
    if (cursor) p.cursor = cursor;
    return p;
  }, [action, resourceType, since, until, cursor]);

  const list = useQuery({
    queryKey: ["audit", scope.id, params],
    queryFn: () => apiClient.listAudit(params),
    retry: false,
  });

  const onPreset = (p: Preset): void => {
    setPreset(p);
    setCursor(undefined);
  };

  const exportCsv = (): void => {
    const items = list.data?.items ?? [];
    if (items.length === 0) return;
    const headers = [
      "at",
      "action",
      "resource_type",
      "resource_name",
      "resource_id",
      "actor_email",
      "actor_path",
    ];
    const rows = items.map((e) => [
      e.at,
      e.action,
      e.resource_type,
      e.resource_name ?? "",
      e.resource_id ?? "",
      e.actor.email ?? "",
      e.actor.partner_path ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Replace colons in the ISO stamp - invalid in filenames on
    // Windows and get silently rewritten by Chrome on download.
    const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    a.download = `audit-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Activity</h1>
          <p className="text-sm text-muted-foreground">
            Every admin action taken in your scope. Expand a row to see what changed.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={(list.data?.items.length ?? 0) === 0}
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Filters: time range + free-text filters + cursor reset. */}
      <div className="space-y-3 rounded-lg border bg-card p-3 shadow-sm sm:p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Time
          </span>
          {(["1h", "24h", "7d", "30d", "all", "custom"] as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPreset(p)}
              className={
                preset === p
                  ? "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                  : "rounded-md border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              }
            >
              {PRESET_LABEL[p]}
            </button>
          ))}
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              From
              <input
                type="datetime-local"
                value={customSince}
                onChange={(e) => setCustomSince(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              To
              <input
                type="datetime-local"
                value={customUntil}
                onChange={(e) => setCustomUntil(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              />
            </label>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setCursor(undefined);
          }}
          className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]"
        >
          <input
            type="text"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Action (e.g. partner.create)"
            aria-label="Filter by action"
            className="rounded-md border bg-background px-3 py-2 font-mono text-sm"
            spellCheck={false}
          />
          <input
            type="text"
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
            placeholder="Resource type (e.g. user)"
            aria-label="Filter by resource type"
            className="rounded-md border bg-background px-3 py-2 font-mono text-sm"
            spellCheck={false}
          />
          <button
            type="submit"
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Apply
          </button>
        </form>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {list.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {list.error instanceof Error ? list.error.message : "Failed to load audit log"}
        </div>
      )}
      {list.data && list.data.items.length === 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground sm:p-6">
          <ScrollText className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          No audit entries match the current filters.
        </div>
      )}
      {list.data && list.data.items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-8 px-3 py-2.5" aria-label="Row actions"></th>
                <th className="px-3 py-2.5">Time</th>
                <th className="px-3 py-2.5">Action</th>
                <th className="px-3 py-2.5">Resource</th>
                <th className="px-3 py-2.5">Actor</th>
                <th className="px-3 py-2.5">Scope</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((e) => (
                <AuditRow key={e.id} entry={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-xs text-muted-foreground">
        <span>
          {list.data ? `Showing ${list.data.items.length} entries` : ""}
          {cursor ? " (continued)" : ""}
        </span>
        <div className="flex gap-2">
          {cursor && (
            <button
              type="button"
              onClick={() => setCursor(undefined)}
              className="rounded-md border px-2 py-1 text-xs hover:bg-muted"
            >
              First page
            </button>
          )}
          {list.data?.next_cursor && (
            <button
              type="button"
              onClick={() => setCursor(list.data.next_cursor ?? undefined)}
              className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditRow(props: { entry: AuditEntry }): JSX.Element {
  const { entry } = props;
  const [open, setOpen] = useState(false);
  const hasDiff = entry.before != null || entry.after != null;
  const hasTechnical = !!(entry.resource_id || entry.correlation_id || entry.ip_address);
  const expandable = hasDiff || hasTechnical;
  const rowId = `audit-detail-${entry.id}`;

  return (
    <>
      <tr className="border-b">
        <td className="px-3 py-2.5 align-top text-muted-foreground">
          {expandable ? (
            // Real button so keyboard / screen-reader users can expand
            // the row; previously onClick on <tr> silently excluded them.
            <button
              type="button"
              aria-expanded={open}
              aria-controls={rowId}
              aria-label={open ? "Collapse details" : "Expand details"}
              onClick={() => setOpen((v) => !v)}
              className="rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : null}
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 align-top font-mono text-xs text-muted-foreground">
          {new Date(entry.at).toLocaleString()}
        </td>
        <td className="px-3 py-2.5 align-top font-mono text-xs font-medium text-primary">
          {entry.action}
        </td>
        <td className="px-3 py-2.5 align-top text-xs text-muted-foreground">
          <span className="font-mono">{entry.resource_type}</span>
          {entry.resource_name ? (
            <span className="ml-1 font-medium text-foreground">{entry.resource_name}</span>
          ) : entry.resource_id ? (
            // Server didn't resolve a friendly label (resource type
            // we don't JOIN, e.g. provider_*) - fall back to the id
            // prefix so the row still distinguishes itself from peers.
            <span className="ml-1 font-mono text-muted-foreground">
              {String(entry.resource_id).slice(0, 8)}…
            </span>
          ) : null}
        </td>
        <td className="break-all px-3 py-2.5 align-top text-xs">
          {(() => {
            const name = [entry.actor.first_name, entry.actor.last_name].filter(Boolean).join(" ");
            if (name) {
              return (
                <>
                  <div className="font-medium text-foreground">{name}</div>
                  {entry.actor.email && (
                    <div className="text-[11px] text-muted-foreground">{entry.actor.email}</div>
                  )}
                </>
              );
            }
            return entry.actor.email ?? entry.actor.user_id ?? "-";
          })()}
        </td>
        <td className="break-all px-3 py-2.5 align-top font-mono text-xs text-muted-foreground">
          {entry.actor.partner_path ?? ""}
        </td>
      </tr>
      {open && (
        <tr id={rowId} className="border-b bg-muted/20">
          <td colSpan={6} className="px-3 py-3">
            {hasDiff && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {entry.before && (
                  <div>
                    <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      before
                    </h4>
                    <pre className="overflow-x-auto rounded-md border bg-background p-2 text-[11px] leading-relaxed">
                      {JSON.stringify(entry.before, null, 2)}
                    </pre>
                  </div>
                )}
                {entry.after && (
                  <div>
                    <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      after
                    </h4>
                    <pre className="overflow-x-auto rounded-md border bg-background p-2 text-[11px] leading-relaxed">
                      {JSON.stringify(entry.after, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            {hasTechnical && (
              <TechnicalDetails>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                  {entry.resource_id && (
                    <>
                      <dt className="font-medium">resource_id</dt>
                      <dd className="break-words font-mono">{entry.resource_id}</dd>
                    </>
                  )}
                  {entry.correlation_id && (
                    <>
                      <dt className="font-medium">correlation_id</dt>
                      <dd className="break-words font-mono">{entry.correlation_id}</dd>
                    </>
                  )}
                  {entry.ip_address && (
                    <>
                      <dt className="font-medium">ip_address</dt>
                      <dd className="break-words font-mono">{entry.ip_address}</dd>
                    </>
                  )}
                </dl>
              </TechnicalDetails>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
